/**
 * O que foi PEDIDO num pedido de aprovação de desconto, para o aviso de quem
 * aprova (solicitação 6125).
 *
 * O aviso mostrava só o multiplicador ("1,20x") e o motivo — quem aprovava não
 * via que o pedido era "20% na tarifa, 50% na bandeira, carência de 90 dias" sem
 * abrir a negociação. Só apresentação: nenhuma regra de desconto passa por aqui.
 *
 * Fonte, da mais fiel para a menos:
 *  1. o registro do pedido em `aprovacoes_desconto` (#5917), que guarda os
 *     percentuais e a carência como foram pedidos — existe desde 22/09/2026;
 *  2. o plano gravado na negociação, para pedidos mais antigos. O plano
 *     "fora da política" não tem percentual fixo, então sem o registro do
 *     pedido o valor fica em branco em vez de aparecer um 0% que não foi pedido.
 */
import { getPlano, isCarenciaSolicitada } from './politicaComercial.ts';

export interface PedidoRegistrado {
    desconto_tarifa: number | null;
    desconto_bandeira: number | null;
    carencia: string | null;
}

export interface ResumoPedidoDesconto {
    plano: string | null;
    foraDaPolitica: boolean;
    /** "20%" ou null quando não há como saber. */
    tarifa: string | null;
    bandeira: string | null;
    /** null quando não foi pedida carência. */
    carencia: string | null;
    fidelidade: string | null;
    /** De onde vieram os percentuais — para o `title` do cartão. */
    fonte: 'pedido' | 'plano' | 'nenhuma';
}

const pct = (v: number | null | undefined) => {
    const n = Number(v);
    if (v === null || v === undefined || !Number.isFinite(n)) return null;
    return `${n.toLocaleString('pt-BR', { maximumFractionDigits: 2 })}%`;
};

export function resumirPedidoDesconto(
    negociacao: { plano_codigo?: string | null; fidelidade?: string | null },
    pedido: PedidoRegistrado | null | undefined,
): ResumoPedidoDesconto {
    const plano = getPlano(negociacao.plano_codigo);
    const doPedido = !!pedido && (pedido.desconto_tarifa != null || pedido.desconto_bandeira != null);
    // Plano fora da política tem descTarifa/descBandeira 0 como marcador, não como valor.
    const doPlano = !doPedido && !!plano && !plano.foraDaPolitica;

    const carenciaTexto = pedido?.carencia ?? null;
    const fidelidade = String(negociacao.fidelidade ?? '').trim();
    return {
        plano: plano?.label ?? null,
        foraDaPolitica: !!plano?.foraDaPolitica,
        tarifa: doPedido ? pct(pedido!.desconto_tarifa) : doPlano ? pct(plano!.descTarifa) : null,
        bandeira: doPedido ? pct(pedido!.desconto_bandeira) : doPlano ? pct(plano!.descBandeira) : null,
        carencia: isCarenciaSolicitada(carenciaTexto) ? carenciaTexto : null,
        fidelidade: fidelidade || null,
        fonte: doPedido ? 'pedido' : doPlano ? 'plano' : 'nenhuma',
    };
}
